import React from 'react';
import { useQuery } from 'react-query';
import { ArrowLeft, Activity, BarChart3, PieChart, TrendingUp, Target, Users, Building } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const OverallStatus = () => {
  // Fetch overall status data
  const { data: overallStatus, isLoading } = useQuery(
    ['overall-status'],
    async () => {
      const response = await axios.get('/api/status/overall');
      return response.data;
    }
  );

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in progress': return 'text-blue-600 bg-blue-100';
      case 'on hold': return 'text-yellow-600 bg-yellow-100';
      case 'not started': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return '✓';
      case 'in progress': return '▶';
      case 'on hold': return '⏸';
      case 'not started': return '○';
      default: return '○';
    }
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-blue-600';
    if (percentage >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Overall Status</h1>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card">
              <div className="flex items-center">
                <Activity className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Total Tasks</h3>
                  <p className="text-2xl font-bold text-blue-600">{overallStatus?.metrics?.totalTasks || 0}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <Target className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Completed</h3>
                  <p className="text-2xl font-bold text-green-600">{overallStatus?.metrics?.completedTasks || 0}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Active Cells</h3>
                  <p className="text-2xl font-bold text-purple-600">{overallStatus?.metrics?.activeCells || 0}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <Building className="h-8 w-8 text-orange-600" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Total Buildings</h3>
                  <p className="text-2xl font-bold text-orange-600">{overallStatus?.metrics?.totalBuildings || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Overall Progress Overview */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              Overall Progress Overview
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Overall Completion Rate</span>
                <span className={`text-lg font-bold ${getCompletionColor(overallStatus?.overallProgress?.completionRate)}`}>
                  {overallStatus?.overallProgress?.completionRate || 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className={`h-4 rounded-full transition-all duration-500 ${getCompletionColor(overallStatus?.overallProgress?.completionRate).replace('text-', 'bg-')}`}
                  style={{ width: `${overallStatus?.overallProgress?.completionRate || 0}%` }}
                ></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-lg font-semibold text-green-600">{overallStatus?.overallProgress?.completedTasks || 0}</div>
                  <div className="text-gray-600">Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-blue-600">{overallStatus?.overallProgress?.inProgressTasks || 0}</div>
                  <div className="text-gray-600">In Progress</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-yellow-600">{overallStatus?.overallProgress?.onHoldTasks || 0}</div>
                  <div className="text-gray-600">On Hold</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-600">{overallStatus?.overallProgress?.notStartedTasks || 0}</div>
                  <div className="text-gray-600">Not Started</div>
                </div>
              </div>
            </div>
          </div>

          {/* Task Status Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Task Status Pie Chart */}
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <PieChart className="h-5 w-5 mr-2" />
                Task Status Distribution
              </h3>
              <div className="space-y-4">
                {overallStatus?.statusDistribution?.map((status) => (
                  <div key={status.status} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full mr-3 ${getStatusColor(status.status).split(' ')[1]}`}></div>
                      <span className="text-sm font-medium text-gray-700">{status.status}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getStatusColor(status.status).split(' ')[1]}`}
                          style={{ width: `${(status.count / status.total) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600 w-12 text-right">{status.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Cell Performance Ranking */}
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Cell Performance Ranking
              </h3>
              <div className="space-y-3">
                {overallStatus?.cellRanking?.map((cell, index) => (
                  <div key={cell.cell_id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${
                        index < 3 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Cell {cell.cell_no}</div>
                        <div className="text-xs text-gray-500">{cell.state_name}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-medium ${getCompletionColor(cell.avg_progress)}`}>
                        {cell.avg_progress}%
                      </div>
                      <div className="text-xs text-gray-500">{cell.completed_tasks}/{cell.total_tasks} tasks</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              Recent Activity
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b-2 border-gray-300 bg-gray-50">
                    <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Date</th>
                    <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Cell</th>
                    <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Task</th>
                    <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Action</th>
                    <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Status</th>
                    <th className="py-3 px-4 font-semibold text-gray-700">Submitted By</th>
                  </tr>
                </thead>
                <tbody>
                  {overallStatus?.recentActivity?.map((activity, index) => (
                    <tr key={index} className="border-b border-gray-200">
                      <td className="py-3 px-4 border-r border-gray-200">{new Date(activity.date).toLocaleDateString()}</td>
                      <td className="py-3 px-4 border-r border-gray-200">Cell {activity.cell_name}</td>
                      <td className="py-3 px-4 border-r border-gray-200">{activity.task_name}</td>
                      <td className="py-3 px-4 border-r border-gray-200">{activity.action}</td>
                      <td className="py-3 px-4 border-r border-gray-200">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                          {getStatusIcon(activity.status)} {activity.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">{activity.user_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Performance Trends */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              Performance Trends (Last 6 Months)
            </h3>
            <div className="space-y-4">
              {overallStatus?.monthlyTrends?.map((month) => (
                <div key={month.month} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{month.month_name}</h4>
                    <span className={`text-sm font-medium ${getCompletionColor(month.completion_rate)}`}>
                      {month.completion_rate}% Complete
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${getCompletionColor(month.completion_rate).replace('text-', 'bg-')}`}
                        style={{ width: `${month.completion_rate}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Tasks: {month.total_tasks}</span>
                      <span>Completed: {month.completed_tasks}</span>
                      <span>In Progress: {month.in_progress_tasks}</span>
                      <span>On Hold: {month.on_hold_tasks}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OverallStatus;
